import type { LeadershipImpact, RoiCostProject } from '../types/leadership';

/**
 * The cost model behind the hero figure - made explicit so a CFO can audit it.
 * Every project's averted cost is DERIVED from these two rates, so the $1.2M is
 * the sum of an auditable breakdown rather than an asserted headline.
 */
const RATE_PER_ENG_DAY_USD = 770; // $200K fully-loaded salary / 260 working days.
const P1_INCIDENT_COST_USD = 48_000; // Modeled response + remediation per P1.

/**
 * The projects behind the averted-cost figure, before their USD columns are
 * derived. Each maps to one of the three recurring patterns, carries the
 * engineer-days and P1 incidents Intera would have saved, and the evidence
 * trail (the artifacts it correlated) that explains why it ran so expensive.
 */
const ROI_PROJECT_SEEDS: Array<
  Omit<RoiCostProject, 'reworkUsd' | 'incidentUsd' | 'avertedUsd'>
> = [
  {
    id: 'payments-idempotency',
    name: 'Payments idempotency migration',
    team: 'Payments Platform',
    patternId: 'migrations-no-rollback',
    patternLabel: 'Migration without rollback',
    engDays: 70,
    p1Count: 6,
    whyItCostSoMuch:
      'The idempotency-key migration shipped with no rollback section, so when the backfill double-applied in production there was no clean way back - the team hand-unwound duplicate charges across nine days while the ticket reopened three times.',
    signals: [
      {
        source: 'jira',
        ref: 'PAY-9281',
        description:
          'Reopened 3× - each "fixed" backfill reintroduced duplicates.',
      },
      {
        source: 'github',
        ref: 'PR #18204',
        description:
          'Migration PR reverted in prod; no rollback path documented.',
      },
      {
        source: 'pagerduty',
        ref: 'INC-2041',
        description: 'P1 duplicate-charge incident 9 days after launch.',
      },
    ],
  },
  {
    id: 'search-index-migration',
    name: 'Search index migration',
    team: 'Search',
    patternId: 'migrations-no-rollback',
    patternLabel: 'Migration without rollback',
    engDays: 60,
    p1Count: 4,
    whyItCostSoMuch:
      'A reindex with no staged rollback corrupted relevance ranking in production. Recovery meant a full rebuild from scratch plus a scramble of hotfixes while search quality degraded for every customer.',
    signals: [
      {
        source: 'github',
        ref: 'PR #19388',
        description:
          'Emergency hotfix stack - no way to revert the reindex cleanly.',
      },
      {
        source: 'jira',
        ref: 'SRCH-1402',
        description:
          'Rollback plan never written before the migration shipped.',
      },
      {
        source: 'pagerduty',
        ref: 'INC-1990 → INC-1993',
        description: 'Four-incident cluster from degraded search relevance.',
      },
    ],
  },
  {
    id: 'risk-scoring-launch',
    name: 'Risk scoring launch',
    team: 'Risk Platform',
    patternId: 'decisions-not-propagated',
    patternLabel: 'Decision not propagated',
    engDays: 68,
    p1Count: 3,
    whyItCostSoMuch:
      'An approved scoring-threshold decision was never linked back to the build ticket, so the team launched against the old threshold. The mismatch reopened the work four times and shipped a scoring bug to production.',
    signals: [
      {
        source: 'confluence',
        ref: 'ADR-31',
        description:
          'Threshold decision approved but never linked to the ticket.',
      },
      {
        source: 'jira',
        ref: 'RISK-771',
        description:
          'Reopened 4× - built against a decision that had already moved.',
      },
      {
        source: 'pagerduty',
        ref: 'INC-2188',
        description: 'P1 mis-scoring incident traced to the stale threshold.',
      },
    ],
  },
  {
    id: 'growth-promo-platform',
    name: 'Growth promo platform',
    team: 'Growth Engineering',
    patternId: 'decisions-not-propagated',
    patternLabel: 'Decision not propagated',
    engDays: 52,
    p1Count: 2,
    whyItCostSoMuch:
      'A launch date moved in Slack but the sprint plan never changed, so the team kept building to the old date. A sprint of promo work landed against a window that had already shifted.',
    signals: [
      {
        source: 'slack',
        ref: '#growth-promo',
        description:
          '"Let us move the promo to the 28th" - never reached the plan.',
      },
      {
        source: 'jira',
        ref: 'GROW-203',
        description: 'Sprint dates unchanged after the launch moved.',
      },
    ],
  },
  {
    id: 'unified-billing-ledger',
    name: 'Unified billing ledger',
    team: 'Core Banking',
    patternId: 'estimates-no-notice',
    patternLabel: 'Estimate not propagated',
    engDays: 96,
    p1Count: 2,
    whyItCostSoMuch:
      'The ledger was re-estimated three times mid-flight and none of it reached the dependent teams. Downstream squads kept building against a date that no longer existed until the whole program slipped.',
    signals: [
      {
        source: 'jira',
        ref: 'BILL-4471',
        description: 'Re-estimated 3× - dependent teams never re-baselined.',
      },
      {
        source: 'slack',
        ref: '#billing-eng',
        description: '"Pushing to Sprint 44" - downstream teams never saw it.',
      },
      {
        source: 'pagerduty',
        ref: 'INC-2210',
        description:
          'P1 from a half-migrated ledger under the slipped timeline.',
      },
    ],
  },
  {
    id: 'analytics-ingestion-revamp',
    name: 'Analytics ingestion revamp',
    team: 'Data Platform',
    patternId: 'estimates-no-notice',
    patternLabel: 'Estimate not propagated',
    engDays: 74,
    p1Count: 1,
    whyItCostSoMuch:
      'A quiet re-estimate on the ingestion rewrite never fanned out, so BI and Analytics kept planning dashboards against the original date - a sprint of work re-planned once the real timeline surfaced.',
    signals: [
      {
        source: 'jira',
        ref: 'DATA-842',
        description:
          'Re-estimate stayed inside the team - downstream never notified.',
      },
      {
        source: 'github',
        ref: 'PR #5102',
        description:
          'Rewrite landed weeks after the dates BI had planned around.',
      },
    ],
  },
];

/** Derive each project's USD columns from the two rates, then sort costliest-first. */
const ROI_COST_PROJECTS: RoiCostProject[] = ROI_PROJECT_SEEDS.map((seed) => {
  const reworkUsd = seed.engDays * RATE_PER_ENG_DAY_USD;
  const incidentUsd = seed.p1Count * P1_INCIDENT_COST_USD;
  return {
    ...seed,
    reworkUsd,
    incidentUsd,
    avertedUsd: reworkUsd + incidentUsd,
  };
}).sort((first, second) => second.avertedUsd - first.avertedUsd);

const ROI_TOTAL_ENG_DAYS = ROI_COST_PROJECTS.reduce(
  (sum, project) => sum + project.engDays,
  0
);
const ROI_TOTAL_P1 = ROI_COST_PROJECTS.reduce(
  (sum, project) => sum + project.p1Count,
  0
);
const ROI_REWORK_USD = ROI_TOTAL_ENG_DAYS * RATE_PER_ENG_DAY_USD;
const ROI_INCIDENT_USD = ROI_TOTAL_P1 * P1_INCIDENT_COST_USD;
const ROI_TOTAL_USD = ROI_REWORK_USD + ROI_INCIDENT_USD;
const ROI_TOTAL_MILLIONS = (ROI_TOTAL_USD / 1_000_000).toFixed(1);

/**
 * The executive impact dataset - the CIO's quarter-in-review. The numbers are
 * the aggregate of the same mistakes the manager surfaces handle one at a time
 * (re-estimates without notice, decisions not propagated, migrations without
 * rollback), rolled up across the whole portfolio so leadership sees three
 * things: the value Intera returned, how widely each mistake recurred, and what
 * it *would* have cost unchecked - now closed at the root.
 */
export const LEADERSHIP_IMPACT: LeadershipImpact = {
  periodLabel: 'Last 90 days',
  headline:
    'Across 36 projects, Intera caught the same handful of execution mistakes again and again - and turned each one into a guardrail. Here is what they would have cost you.',
  roi: {
    eyebrow: 'Financial impact',
    avertedValue: ROI_TOTAL_MILLIONS,
    avertedUnit: 'M',
    caption:
      'In rework, slipped timelines and incident response Intera averted across the portfolio this quarter - the same mistakes, caught before they cost anything.',
    stats: [
      {
        id: 'eng-days',
        value: String(ROI_TOTAL_ENG_DAYS),
        label: 'Eng-days saved',
      },
      {
        id: 'p1',
        value: String(ROI_TOTAL_P1),
        label: 'P1 incidents prevented',
      },
      { id: 'multiple', value: '≈10×', label: 'Return on Intera' },
    ],
    footnote:
      'Averted cost = engineer-days saved × a $770 fully-loaded day, plus prevented P1 incidents × $48K each. Return compares value averted to Intera’s cost over the same 90 days.',
    deepDiveCta: 'See the projects behind this number',
    formula: {
      ratePerEngDayUsd: RATE_PER_ENG_DAY_USD,
      p1CostUsd: P1_INCIDENT_COST_USD,
      totalEngDays: ROI_TOTAL_ENG_DAYS,
      totalP1: ROI_TOTAL_P1,
      reworkUsd: ROI_REWORK_USD,
      incidentUsd: ROI_INCIDENT_USD,
      totalUsd: ROI_TOTAL_USD,
    },
    projects: ROI_COST_PROJECTS,
  },
  kpis: [
    {
      id: 'gaps-caught',
      label: 'Execution gaps caught',
      value: '187',
      caption: 'surfaced before they reached delivery',
      trendLabel: '+14% vs Q1',
      trendDirection: 'up',
      tone: 'value',
    },
    {
      id: 'days-saved',
      label: 'Engineering-days saved',
      value: '420',
      caption: 'rework and slip never spent',
      trendLabel: '≈ 2.1 engineers / quarter',
      trendDirection: 'flat',
      tone: 'value',
    },
    {
      id: 'p1-prevented',
      label: 'P1 incidents prevented',
      value: '18',
      caption: 'caught in planning, not in prod',
      tone: 'value',
    },
    {
      id: 'projects-protected',
      label: 'Projects protected',
      value: '36',
      caption: 'every active program, continuously watched',
      trendLabel: 'Full portfolio coverage',
      trendDirection: 'flat',
      tone: 'value',
    },
  ],
  patterns: [
    {
      id: 'estimates-no-notice',
      codename: 'Pattern 01',
      name: 'Estimates changed without telling downstream teams',
      summary:
        'A shared-pipeline team re-estimated mid-flight and nobody downstream heard — so dependent teams kept building against a date that no longer existed.',
      severity: 'high',
      timesCaught: 24,
      projectsSpanned: 16,
      teams: 'Data Platform · BI · Analytics',
      avertedValue: '~180',
      avertedUnit: 'eng-days',
      avertedCaption: 'of stale work and re-planning, if it had run unseen',
      avertedSecondary: '9 near-missed deadlines',
      enforcedName: 'Same-day re-estimate notice',
      enforcedDetail:
        'A re-estimate on a shared service now fans out to every dependent team the same day and re-baselines their plan automatically — no one has to remember to post.',
      guardingLabel: 'Now guarding 12 teams',
      instancesGuarded: 24,
    },
    {
      id: 'decisions-not-propagated',
      codename: 'Pattern 02',
      name: 'Launch & architecture decisions never reached the team',
      summary:
        'A date or design decision changed at the top and the teams building against it found out late — sometimes a full sprint of work after it was already void.',
      severity: 'high',
      timesCaught: 18,
      projectsSpanned: 12,
      teams: 'Growth · Risk Platform',
      avertedValue: '~120',
      avertedUnit: 'eng-days',
      avertedCaption: 'of work built against decisions that had already moved',
      avertedSecondary: '2 launches re-scoped in time',
      enforcedName: 'Decision-change routing',
      enforcedDetail:
        'When a launch date or ADR changes, every team building against it is pulled in automatically and their work is re-baselined — the decision reaches the floor the day it is made.',
      guardingLabel: 'Now guarding 8 teams',
      instancesGuarded: 18,
    },
    {
      id: 'migrations-no-rollback',
      codename: 'Pattern 03',
      name: 'Migrations shipped without a rollback plan',
      summary:
        'A schema migration reached review with no rollback section, so when one failed in staging there was no clean path back and the recovery was manual.',
      severity: 'medium',
      timesCaught: 12,
      projectsSpanned: 9,
      teams: 'Core Banking · Platform API',
      avertedValue: '~70',
      avertedUnit: 'eng-days',
      avertedCaption: 'of hand-unwinding half-applied migrations, if unchecked',
      avertedSecondary: '4 P1 incidents avoided',
      enforcedName: 'Rollback-plan gate in CI',
      enforcedDetail:
        'A migration PR with no documented rollback section can no longer reach review — the playbook is now a hard check in the pipeline, not a line in a wiki.',
      guardingLabel: 'Now guarding 6 teams',
      instancesGuarded: 12,
    },
  ],
  trend: {
    peak: 44,
    note: 'Monthly execution gaps surfaced. The curve bends down as each mistake becomes a guardrail — there is simply less left to catch.',
    points: [
      { month: 'Jan', surfaced: 41, resolvedRate: 38 },
      { month: 'Feb', surfaced: 38, resolvedRate: 44 },
      { month: 'Mar', surfaced: 44, resolvedRate: 41 },
      { month: 'Apr', surfaced: 31, resolvedRate: 62, isIntervention: true },
      { month: 'May', surfaced: 22, resolvedRate: 74 },
      { month: 'Jun', surfaced: 16, resolvedRate: 83 },
    ],
  },
  closingHeadline: 'Fewer to catch every month.',
  closingBlurb:
    'Each recurring mistake Intera turned into a guardrail is a class of failure your teams no longer repeat. The work isn’t to watch harder — it’s that the roots are closing, and the portfolio is getting quieter on its own.',
};
